import { Grant } from 'components/types';

export default function handleGrants(grants: Grant[]) {
  const has = (key: string) => Boolean(grants.find(({ name }) => name === key));
  const hasAssignOrgGrants = has('ASSIGN_ORG_GRANTS');
  const hasClustersOrgGrant = has('COSTCENTER_CLUSTERS');
  const hasPoolsOrgGrant = has('FLEX_PORTAL_POOL');
  const hasTravelCostCalculationAccess = has('TRAVELCOST_CALCULATION');
  const hasRosterPortalPlanModusAccess = has('ROSTER_PORTAL_ROSTER_PLAN_MODUS');
  const hasRosterAccess =
    has('ROSTER_PORTAL') &&
    (has('ROSTER_PORTAL_VIEW') || has('ROSTER_PORTAL_VIEW_NO_TOOLTIP'));
  const hasRosterPortalMessagesAccess =
    hasRosterPortalPlanModusAccess ||
    (hasRosterAccess &&
      !has('ROSTER_PORTAL_ROSTER_VIEW_MODUS_WITHOUT_MESSAGESBOX'));
  const hasKidsPlan = has('KIDS_PLAN');
  const hasEmployeeCardEditPreferences = has('EMPLOYEECARD_EDIT_PREFERENCES');
  const hasEmbeddedDashboard = has('EMBEDDED_ANALYTICS_MODULE');
  const hasEmployeeCard = has('EMPLOYEECARD');
  const hasEmployeePortalSelfMatching = has('EMPLOYEE_PORTAL_SELF_MATCHING');
  const hasEmployeePortalSelfRosteringConnect = has(
    'EMPLOYEE_PORTAL_SELF_ROSTERING_LINK'
  );
  const hasEmployeePortalSelfRosteringSubscribe = has(
    'EMPLOYEE_PORTAL_SELF_ROSTERING_SUBSCRIBE'
  );
  const hasFeatureToggle = has('FEATURE_TOGGLE');
  const hasFeatureToggleExpenseClaims = has('FEATURE_TOGGLE_EXPENSE_CLAIMS');
  const hasAbsenceNoticesViewAccess =
    has('ABSENCE_NOTICES_MODULE') && has('ABSENCE_NOTICES_VIEW');
  const hasAutoAskDutyRoundsViewAccess =
    has('DUTY_AUTO_ASK_EMPLOYEES') && has('DUTY_AUTO_ASK_EMPLOYEES_VIEW');
  const hasCapacityPortalEditAccess = has('CAPACITY_PORTAL_EDIT');
  const hasCapacityFinancialAccess = has('CAPACITY_FINANCIAL');
  const hasFlexPortalCapacityEdit = has('FLEX_PORTAL_CAPACITY_EDIT');
  const hasRealizationAccess = has('ADMIN_REALIZATION');
  const hasActivityPlanningViewAccess =
    has('ACTIVITY_PLANNING') &&
    (has('ACTIVITY_PLANNING_VIEW') || has('ACTIVITY_PLANNING_EDIT'));
  const hasActivityPlanningEditAccess =
    has('ACTIVITY_PLANNING') && has('ACTIVITY_PLANNING_EDIT');
  const hasKidsPlanAccess = hasKidsPlan && hasRosterAccess;
  const hasWorkPlacePlanningViewAccess =
    has('WORKPLACE_PLANNING') &&
    (has('WORKPLACE_PLANNING_VIEW') || has('WORKPLACE_PLANNING_EDIT'));
  const hasWorkPlacePlanningEditAccess =
    has('WORKPLACE_PLANNING') && has('WORKPLACE_PLANNING_EDIT');
  const hasRegionalPoolOrganisationAccess = has('REGIONAL_POOL_ORGANISATION');
  const hasAccessToAdminTablesReact = has('FEATURE_TOGGLE_ADMIN_TABLES_REACT');
  const hasAccessToAdminGrantsReact = has('FEATURE_TOGGLE_ADMIN_GRANTS_REACT');
  const hasAccessToAdminSettingsReact = has(
    'FEATURE_TOGGLE_ADMIN_SETTINGS_REACT'
  );
  const hasAccessToAdminProceduresReact = has(
    'FEATURE_TOGGLE_ADMIN_PROCEDURES_REACT'
  );
  const hasMyActivitiesProjectAccess = has('PROJECT_PLANNING');
  const hasMyActivitiesAccess = has('MY_ACTIVITIES_VIEW');
  const hasManageActivities = has('MANAGE_ACTIVITIES');
  const hasManageRoutes = has('MANAGE_APPOINTMENT_GROUPS');

  const hasManageProjectsAccess =
    has('PROJECT_PLANNING') &&
    (has('MANAGE_ALL_PROJECTS') || has('MANAGE_OWN_PROJECTS'));
  const hasManageOwnProjectsAccess =
    has('PROJECT_PLANNING') && has('MANAGE_OWN_PROJECTS');
  const hasProjectTeamAccess =
    has('PROJECT_PLANNING') && has('PROJECT_PLANNING_TEAM_VIEW');
  const hasEmploymentAdditionalPropertiesEdit = has(
    'EMPLOYMENT_ADDITIONAL_PROPERTIES_EDIT'
  );
  const hasBasicReportAccess = has('BASIC_REPORTS');
  const hasAdvancedReportAccess = has('ADVANCED_REPORTS');
  const hasAdminReportAccess = has('ADMIN_REPORTS');
  const hasBalanceReportAccess = has('BALANCE');
  const hasServiceDeskReportAccess = has('ASSIGN_ORG_GRANTS');

  const hasManageOwnProjectAccess = has('MANAGE_OWN_PROJECTS');
  const hasManageAllProjectAccess = has('MANAGE_ALL_PROJECTS');
  const hasProjectPlanningTeamViewAccess = has('PROJECT_PLANNING_TEAM_VIEW');
  const hasCostCalculationExternals = has('COST_CALCULATION_EXTERNALS');
  const hasDatabaseViewAccess = has('DATABASE_BI_VIEWS');
  const hasFlexCapacityView =
    has('CAPACITY_PORTAL') && has('FLEX_PORTAL_CAPACITY_VIEW');
  const hasTravelMovementsFeature = has('TRAVEL_MOVEMENT_MODULE');

  return {
    hasAccessToAdminGrantsReact,
    hasAccessToAdminProceduresReact,
    hasAccessToAdminSettingsReact,
    hasAccessToAdminTablesReact,
    hasClustersOrgGrant,
    hasPoolsOrgGrant,
    hasTravelCostCalculationAccess,
    hasActivityPlanningEditAccess,
    hasActivityPlanningViewAccess,
    hasAdminReportAccess,
    hasAdvancedReportAccess,
    hasAssignOrgGrants,
    hasAutoAskDutyRoundsViewAccess,
    hasBalanceReportAccess,
    hasBasicReportAccess,
    hasCapacityFinancialAccess,
    hasCapacityPortalEditAccess,
    hasCostCalculationExternals,
    hasDatabaseViewAccess,
    hasEmbeddedDashboard,
    hasEmployeeCard,
    hasEmployeeCardEditPreferences,
    hasEmployeePortalSelfMatching,
    hasEmployeePortalSelfRosteringConnect,
    hasEmployeePortalSelfRosteringSubscribe,
    hasEmploymentAdditionalPropertiesEdit,
    hasFeatureToggle,
    hasFeatureToggleExpenseClaims,
    hasFlexPortalCapacityEdit,
    hasKidsPlan,
    hasKidsPlanAccess,
    hasManageActivities,
    hasManageRoutes,
    hasManageAllProjectAccess,
    hasManageOwnProjectAccess,
    hasManageOwnProjectsAccess,
    hasManageProjectsAccess,
    hasMyActivitiesAccess,
    hasMyActivitiesProjectAccess,
    hasProjectPlanningTeamViewAccess,
    hasProjectTeamAccess,
    hasRealizationAccess,
    hasRegionalPoolOrganisationAccess,
    hasRosterAccess,
    hasRosterPortalMessagesAccess,
    hasRosterPortalPlanModusAccess,
    hasServiceDeskReportAccess,
    hasWorkPlacePlanningEditAccess,
    hasWorkPlacePlanningViewAccess,
    hasFlexCapacityView,
    hasAbsenceNoticesViewAccess,
    hasTravelMovementsFeature
  };
}

export type UserGrants = ReturnType<typeof handleGrants>;
